import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Loader2, 
  RefreshCw, 
  Activity, 
  Users, 
  FileText, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  Database,
  Server,
  LogIn,
  LogOut,
  UserPlus,
  Edit,
  Trash2,
  Printer,
  User,
  KeyRound,
  Download,
  Upload as UploadIcon,
  FileDown,
  BarChart3,
} from 'lucide-react';
import { format, subDays, subHours, startOfDay, endOfDay } from 'date-fns';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

interface AuditLog {
  id: string;
  user_id: string | null;
  username: string | null;
  event_type: string;
  event_category: string;
  description: string | null;
  metadata: Record<string, unknown>;
  ip_address: string | null;
  success: boolean;
  created_at: string;
}

interface UsageStats {
  totalUsers: number;
  activeToday: number;
  totalTemplates: number;
  globalTemplates: number;
  totalPresets: number;
  activePresets: number;
}

interface ActivitySummary {
  logins: number;
  failedLogins: number;
  templatesCreated: number;
  templatesPrinted: number;
  usersCreated: number;
}

interface PrintStats {
  totalPrints: number;
  printsPerUser: { username: string; count: number }[];
  printsPerTemplate: { template_name: string; count: number }[];
  printsPerDay: { date: string; count: number }[];
  printsBySource: { source: string; count: number }[];
}

const EVENT_ICONS: Record<string, React.ReactNode> = {
  'login': <LogIn className="w-4 h-4" />,
  'logout': <LogOut className="w-4 h-4" />,
  'user_created': <UserPlus className="w-4 h-4" />,
  'template_created': <FileText className="w-4 h-4" />,
  'template_updated': <Edit className="w-4 h-4" />,
  'template_deleted': <Trash2 className="w-4 h-4" />,
  'template_saved': <FileText className="w-4 h-4" />,
  'print': <Printer className="w-4 h-4" />,
  'kiosk_print': <Printer className="w-4 h-4" />,
  'batch_export': <FileDown className="w-4 h-4" />,
  'pdf_export': <FileDown className="w-4 h-4" />,
  'csv_import': <UploadIcon className="w-4 h-4" />,
  'json_import': <UploadIcon className="w-4 h-4" />,
  'password_changed': <KeyRound className="w-4 h-4" />,
  'error': <AlertTriangle className="w-4 h-4" />,
};

const CATEGORY_COLORS: Record<string, string> = {
  'auth': 'bg-primary/10 text-primary',
  'template': 'bg-accent text-accent-foreground',
  'user': 'bg-secondary text-secondary-foreground',
  'print': 'bg-muted text-muted-foreground',
  'data': 'bg-primary/10 text-primary',
  'error': 'bg-destructive/10 text-destructive',
  'system': 'bg-muted text-muted-foreground',
};

export const MonitoringDashboard: React.FC = () => {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [activitySummary, setActivitySummary] = useState<ActivitySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState('24h');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [userFilter, setUserFilter] = useState<string>('all');
  const [allUsers, setAllUsers] = useState<{ user_id: string; username: string }[]>([]);
  const [userActivityLogs, setUserActivityLogs] = useState<AuditLog[]>([]);
  const [userActivitySummary, setUserActivitySummary] = useState<{
    loginCount: number;
    lastActive: string | null;
    totalPrints: number;
    totalTemplateSaves: number;
  } | null>(null);
  const [printStats, setPrintStats] = useState<PrintStats | null>(null);
  const [exportingLogs, setExportingLogs] = useState(false);

  const getTimeRangeFilter = useCallback(() => {
    const now = new Date();
    switch (timeRange) {
      case '1h': return subHours(now, 1);
      case '6h': return subHours(now, 6);
      case '24h': return subHours(now, 24);
      case '7d': return subDays(now, 7);
      case '30d': return subDays(now, 30);
      default: return subHours(now, 24);
    }
  }, [timeRange]);

  const fetchPrintStats = useCallback(async () => {
    const startTime = getTimeRangeFilter();
    try {
      const { data: prints } = await supabase
        .from('label_prints')
        .select('id, user_id, template_name, print_source, copies, created_at')
        .gte('created_at', startTime.toISOString())
        .eq('is_deleted', false)
        .order('created_at', { ascending: false })
        .limit(1000);

      if (!prints || prints.length === 0) {
        setPrintStats({ totalPrints: 0, printsPerUser: [], printsPerTemplate: [], printsPerDay: [], printsBySource: [] });
        return;
      }

      // Get usernames
      const userIds = [...new Set(prints.map(p => p.user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, username')
        .in('user_id', userIds);
      const usernameMap = new Map((profiles || []).map(p => [p.user_id, p.username]));

      // Aggregate per user
      const userCounts: Record<string, number> = {};
      prints.forEach(p => {
        const name = usernameMap.get(p.user_id) || 'Unknown';
        userCounts[name] = (userCounts[name] || 0) + 1;
      });
      const printsPerUser = Object.entries(userCounts)
        .map(([username, count]) => ({ username, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Aggregate per template
      const templateCounts: Record<string, number> = {};
      prints.forEach(p => {
        templateCounts[p.template_name] = (templateCounts[p.template_name] || 0) + 1;
      });
      const printsPerTemplate = Object.entries(templateCounts)
        .map(([template_name, count]) => ({ template_name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Aggregate per day
      const dayCounts: Record<string, number> = {};
      prints.forEach(p => {
        const day = format(new Date(p.created_at), 'yyyy-MM-dd');
        dayCounts[day] = (dayCounts[day] || 0) + 1;
      });
      const printsPerDay = Object.entries(dayCounts)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));

      // Aggregate per source
      const sourceCounts: Record<string, number> = {};
      prints.forEach(p => {
        sourceCounts[p.print_source] = (sourceCounts[p.print_source] || 0) + 1;
      });
      const printsBySource = Object.entries(sourceCounts)
        .map(([source, count]) => ({ source, count }))
        .sort((a, b) => b.count - a.count);

      setPrintStats({
        totalPrints: prints.length,
        printsPerUser,
        printsPerTemplate,
        printsPerDay,
        printsBySource,
      });
    } catch (e) {
      console.error('Failed to fetch print stats:', e);
    }
  }, [getTimeRangeFilter]);

  const exportAuditLogs = useCallback(async () => {
    setExportingLogs(true);
    try {
      const startTime = getTimeRangeFilter();
      let query = supabase
        .from('audit_logs')
        .select('*')
        .gte('created_at', startTime.toISOString())
        .order('created_at', { ascending: false })
        .limit(1000);

      if (categoryFilter !== 'all') {
        query = query.eq('event_category', categoryFilter);
      }

      const { data: logs, error } = await query;
      if (error) throw error;
      if (!logs || logs.length === 0) {
        toast.info('No audit logs to export');
        return;
      }

      const headers = ['Timestamp', 'Username', 'Event Type', 'Category', 'Description', 'Success', 'IP Address', 'User Agent'];
      const rows = logs.map(log => [
        log.created_at,
        log.username || '',
        log.event_type,
        log.event_category,
        (log.description || '').replace(/"/g, '""'),
        log.success ? 'true' : 'false',
        log.ip_address || '',
        (log.user_agent || '').replace(/"/g, '""'),
      ]);

      const csv = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-logs-${format(new Date(), 'yyyy-MM-dd-HHmm')}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${logs.length} audit log entries`);
    } catch (e) {
      console.error('Failed to export audit logs:', e);
      toast.error('Failed to export audit logs');
    } finally {
      setExportingLogs(false);
    }
  }, [getTimeRangeFilter, categoryFilter]);

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    const startTime = getTimeRangeFilter();

    try {
      // Fetch audit logs
      let logsQuery = supabase
        .from('audit_logs')
        .select('*')
        .gte('created_at', startTime.toISOString())
        .order('created_at', { ascending: false })
        .limit(100);

      if (categoryFilter !== 'all') {
        logsQuery = logsQuery.eq('event_category', categoryFilter);
      }

      const { data: logs } = await logsQuery;
      setAuditLogs((logs || []) as AuditLog[]);

      // Fetch all users for the user filter dropdown
      const { data: usersData } = await supabase
        .from('profiles')
        .select('user_id, username')
        .order('username');
      setAllUsers((usersData || []) as { user_id: string; username: string }[]);

      // Fetch usage stats
      const [
        { count: totalUsers },
        { count: totalTemplates },
        { count: globalTemplates },
        { count: totalPresets },
        { count: activePresets },
      ] = await Promise.all([
        supabase.from('profiles').select('*', { count: 'exact', head: true }),
        supabase.from('templates').select('*', { count: 'exact', head: true }),
        supabase.from('templates').select('*', { count: 'exact', head: true }).eq('is_global', true),
        supabase.from('template_presets').select('*', { count: 'exact', head: true }),
        supabase.from('template_presets').select('*', { count: 'exact', head: true }).eq('is_active', true),
      ]);

      // Count active users today based on audit logs
      const todayStart = startOfDay(new Date());
      const { data: activeUsersData } = await supabase
        .from('audit_logs')
        .select('user_id')
        .gte('created_at', todayStart.toISOString())
        .not('user_id', 'is', null);
      
      const uniqueActiveUsers = new Set(activeUsersData?.map(l => l.user_id) || []);

      setUsageStats({
        totalUsers: totalUsers || 0,
        activeToday: uniqueActiveUsers.size,
        totalTemplates: totalTemplates || 0,
        globalTemplates: globalTemplates || 0,
        totalPresets: totalPresets || 0,
        activePresets: activePresets || 0,
      });

      // Calculate activity summary from logs
      const logsInRange = logs || [];
      setActivitySummary({
        logins: logsInRange.filter(l => l.event_type === 'login' && l.success).length,
        failedLogins: logsInRange.filter(l => l.event_type === 'login' && !l.success).length,
        templatesCreated: logsInRange.filter(l => l.event_type === 'template_created' || l.event_type === 'template_saved').length,
        templatesPrinted: logsInRange.filter(l => l.event_type === 'print' || l.event_type === 'kiosk_print').length,
        usersCreated: logsInRange.filter(l => l.event_type === 'user_created').length,
      });

      // Fetch print stats
      await fetchPrintStats();
    } catch (e) {
      console.error('Failed to fetch monitoring data:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getTimeRangeFilter, categoryFilter, fetchPrintStats]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch user-specific activity when userFilter changes
  useEffect(() => {
    if (userFilter === 'all') {
      setUserActivityLogs([]);
      setUserActivitySummary(null);
      return;
    }

    const fetchUserActivity = async () => {
      const startTime = getTimeRangeFilter();
      const { data: logs } = await supabase
        .from('audit_logs')
        .select('*')
        .eq('user_id', userFilter)
        .gte('created_at', startTime.toISOString())
        .order('created_at', { ascending: false })
        .limit(200);

      const typedLogs = (logs || []) as AuditLog[];
      setUserActivityLogs(typedLogs);

      // Calculate user summary
      const loginCount = typedLogs.filter(l => l.event_type === 'login' && l.success).length;
      const lastActive = typedLogs.length > 0 ? typedLogs[0].created_at : null;
      const totalPrints = typedLogs.filter(l => ['print', 'kiosk_print', 'batch_export', 'pdf_export'].includes(l.event_type)).length;
      const totalTemplateSaves = typedLogs.filter(l => l.event_type === 'template_saved').length;

      setUserActivitySummary({ loginCount, lastActive, totalPrints, totalTemplateSaves });
    };

    fetchUserActivity();
  }, [userFilter, getTimeRangeFilter]);

  const formatTimestamp = (timestamp: string) => {
    return format(new Date(timestamp), 'MMM d, HH:mm:ss');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1h">Last hour</SelectItem>
              <SelectItem value="6h">Last 6 hours</SelectItem>
              <SelectItem value="24h">Last 24 hours</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              <SelectItem value="auth">Authentication</SelectItem>
              <SelectItem value="template">Templates</SelectItem>
              <SelectItem value="user">Users</SelectItem>
              <SelectItem value="print">Printing</SelectItem>
              <SelectItem value="error">Errors</SelectItem>
              <SelectItem value="system">System</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={exportAuditLogs}
            disabled={exportingLogs}
          >
            {exportingLogs ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-muted-foreground" />
              <CardDescription>Total Users</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{usageStats?.totalUsers || 0}</div>
            <p className="text-xs text-muted-foreground">
              {usageStats?.activeToday || 0} active today
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <CardDescription>Templates</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{usageStats?.totalTemplates || 0}</div>
            <p className="text-xs text-muted-foreground">
              {usageStats?.globalTemplates || 0} global
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <LogIn className="w-4 h-4 text-muted-foreground" />
              <CardDescription>Logins ({timeRange})</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activitySummary?.logins || 0}</div>
            {(activitySummary?.failedLogins || 0) > 0 && (
              <p className="text-xs text-destructive">
                {activitySummary?.failedLogins} failed
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Printer className="w-4 h-4 text-muted-foreground" />
              <CardDescription>Prints ({timeRange})</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{printStats?.totalPrints || 0}</div>
            <p className="text-xs text-muted-foreground">
              {printStats?.printsBySource.map(s => `${s.count} ${s.source}`).join(', ') || 'No prints'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Activity Feed and Error Log */}
      <Tabs defaultValue="activity" className="space-y-4">
        <TabsList>
          <TabsTrigger value="activity" className="gap-2">
            <Activity className="w-4 h-4" />
            Activity Log
          </TabsTrigger>
          <TabsTrigger value="errors" className="gap-2">
            <AlertTriangle className="w-4 h-4" />
            Errors
          </TabsTrigger>
          <TabsTrigger value="print-stats" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Print Statistics
          </TabsTrigger>
          <TabsTrigger value="stats" className="gap-2">
            <TrendingUp className="w-4 h-4" />
            System
          </TabsTrigger>
          <TabsTrigger value="user-activity" className="gap-2">
            <User className="w-4 h-4" />
            User Activity
          </TabsTrigger>
        </TabsList>

        <TabsContent value="activity">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Activity</CardTitle>
              <CardDescription>
                User actions and system events from the last {timeRange}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {auditLogs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No activity logs found</p>
                  <p className="text-sm">Activity will appear here once users start using the app</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {auditLogs.map((log) => (
                      <div
                        key={log.id}
                        className={`flex items-start gap-3 p-3 rounded-lg border ${
                          log.success ? 'bg-muted/30' : 'bg-destructive/5 border-destructive/20'
                        }`}
                      >
                        <div className={`p-2 rounded-full ${CATEGORY_COLORS[log.event_category] || 'bg-muted'}`}>
                          {EVENT_ICONS[log.event_type] || <Activity className="w-4 h-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{log.event_type.replace(/_/g, ' ')}</span>
                            <Badge variant="outline" className="text-xs">
                              {log.event_category}
                            </Badge>
                            {!log.success && (
                              <Badge variant="destructive" className="text-xs">Failed</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground truncate">
                            {log.description || `By ${log.username || 'Unknown user'}`}
                          </p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTimestamp(log.created_at)}
                            {log.username && ` • ${log.username}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="errors">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Error Log</CardTitle>
              <CardDescription>
                Failed operations and system errors
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                const errorLogs = auditLogs.filter(l => !l.success || l.event_category === 'error');
                if (errorLogs.length === 0) {
                  return (
                    <div className="text-center py-8 text-muted-foreground">
                      <CheckCircle className="w-8 h-8 mx-auto mb-2 text-primary" />
                      <p className="text-primary">No errors found</p>
                      <p className="text-sm">All operations completed successfully</p>
                    </div>
                  );
                }
                return (
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-2">
                      {errorLogs.map((log) => (
                        <div
                          key={log.id}
                          className="flex items-start gap-3 p-3 rounded-lg border bg-destructive/5 border-destructive/20"
                        >
                          <div className="p-2 rounded-full bg-destructive/10">
                            <XCircle className="w-4 h-4 text-destructive" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-destructive">
                                {log.event_type.replace(/_/g, ' ')}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {log.description || 'No description available'}
                            </p>
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatTimestamp(log.created_at)}
                              {log.username && ` • ${log.username}`}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="print-stats">
          <div className="space-y-4">
            {!printStats || printStats.totalPrints === 0 ? (
              <Card>
                <CardContent className="py-8">
                  <div className="text-center text-muted-foreground">
                    <Printer className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No print data found for the selected time range</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Print volume by day */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-primary" />
                      <CardTitle className="text-lg">Print Volume by Day</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {printStats.printsPerDay.map((day) => {
                        const maxCount = Math.max(...printStats.printsPerDay.map(d => d.count));
                        const widthPercent = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                        return (
                          <div key={day.date} className="flex items-center gap-3">
                            <span className="text-sm text-muted-foreground w-24 shrink-0">
                              {format(new Date(day.date), 'MMM d')}
                            </span>
                            <div className="flex-1 h-6 bg-muted/30 rounded overflow-hidden">
                              <div
                                className="h-full bg-primary/20 rounded flex items-center px-2"
                                style={{ width: `${Math.max(widthPercent, 5)}%` }}
                              >
                                <span className="text-xs font-medium">{day.count}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                <div className="grid gap-4 sm:grid-cols-2">
                  {/* Top users */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-primary" />
                        <CardTitle className="text-lg">Top Users</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {printStats.printsPerUser.map((item, i) => (
                          <div key={item.username} className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground w-5">{i + 1}.</span>
                              <span className="text-sm">{item.username}</span>
                            </div>
                            <Badge variant="secondary">{item.count} prints</Badge>
                          </div>
                        ))}
                        {printStats.printsPerUser.length === 0 && (
                          <p className="text-sm text-muted-foreground">No data</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Top templates */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-primary" />
                        <CardTitle className="text-lg">Top Templates</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {printStats.printsPerTemplate.map((item, i) => (
                          <div key={item.template_name} className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground w-5">{i + 1}.</span>
                              <span className="text-sm truncate max-w-[200px]">{item.template_name}</span>
                            </div>
                            <Badge variant="secondary">{item.count} prints</Badge>
                          </div>
                        ))}
                        {printStats.printsPerTemplate.length === 0 && (
                          <p className="text-sm text-muted-foreground">No data</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Print source breakdown */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Printer className="w-5 h-5 text-primary" />
                      <CardTitle className="text-lg">Print Sources</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-4">
                      {printStats.printsBySource.map((item) => (
                        <div key={item.source} className="flex items-center gap-2 p-3 rounded-lg border bg-muted/30">
                          <span className="text-2xl font-bold">{item.count}</span>
                          <span className="text-sm text-muted-foreground capitalize">{item.source}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="stats">
          <div className="grid gap-4 sm:grid-cols-2">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-primary" />
                  <CardTitle className="text-lg">Database Usage</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">User profiles</span>
                  <span className="font-medium">{usageStats?.totalUsers || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Templates</span>
                  <span className="font-medium">{usageStats?.totalTemplates || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Global templates</span>
                  <span className="font-medium">{usageStats?.globalTemplates || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Template presets</span>
                  <span className="font-medium">{usageStats?.totalPresets || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Active presets</span>
                  <span className="font-medium">{usageStats?.activePresets || 0}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Server className="w-5 h-5 text-primary" />
                  <CardTitle className="text-lg">System Health</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Database</span>
                  <Badge variant="secondary">Online</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Authentication</span>
                  <Badge variant="secondary">Online</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Edge Functions</span>
                  <Badge variant="secondary">Online</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Realtime</span>
                  <Badge variant="secondary">Online</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="user-activity">
          <div className="space-y-4">
            {/* User selector */}
            <div className="flex items-center gap-4">
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All users</SelectItem>
                  {allUsers.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {userFilter === 'all' ? (
              <Card>
                <CardContent className="py-8">
                  <div className="text-center text-muted-foreground">
                    <User className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Select a user to view their activity</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* User summary cards */}
                {userActivitySummary && (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription>Logins ({timeRange})</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{userActivitySummary.loginCount}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription>Last Active</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="text-sm font-medium">
                          {userActivitySummary.lastActive
                            ? format(new Date(userActivitySummary.lastActive), 'MMM d, HH:mm')
                            : 'Never'}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription>Total Prints</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{userActivitySummary.totalPrints}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription>Template Saves</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{userActivitySummary.totalTemplateSaves}</div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* User activity timeline */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Activity Timeline</CardTitle>
                    <CardDescription>
                      Activity for {allUsers.find(u => u.user_id === userFilter)?.username || 'selected user'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {userActivityLogs.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No activity found for this user in the selected time range</p>
                      </div>
                    ) : (
                      <ScrollArea className="h-[400px]">
                        <div className="space-y-2">
                          {userActivityLogs.map((log) => (
                            <div
                              key={log.id}
                              className={`flex items-start gap-3 p-3 rounded-lg border ${
                                log.success ? 'bg-muted/30' : 'bg-destructive/5 border-destructive/20'
                              }`}
                            >
                              <div className={`p-2 rounded-full ${CATEGORY_COLORS[log.event_category] || 'bg-muted'}`}>
                                {EVENT_ICONS[log.event_type] || <Activity className="w-4 h-4" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium">{log.event_type.replace(/_/g, ' ')}</span>
                                  <Badge variant="outline" className="text-xs">
                                    {log.event_category}
                                  </Badge>
                                  {!log.success && (
                                    <Badge variant="destructive" className="text-xs">Failed</Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground truncate">
                                  {log.description || 'No description'}
                                </p>
                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {format(new Date(log.created_at), 'MMM d, HH:mm:ss')}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
